/**
 * The languages a note may be captured in, and the three facts a screen needs about each.
 *
 * Authority: `um-app/08-native-language.md` — *"The mockup demonstrates Korean. **The build
 * must be language-agnostic**, driven by the per-UM **Preferred Language** field in
 * Configuration §04 App Access (decision L2). Korean is a worked example, not the feature
 * (gap A1)"* — and its §6, which requires every Korean-specific string in the copy inventory
 * to be *"templated on the UM's Preferred Language"*; ruling Q-08a (2026-08-11, accepted),
 * which names the day-one set; migration 001's `preferred_language` enum and migration 008's
 * `app_user.preferred_language`, whose eight labels this table is keyed by.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS TABLE EXISTS AT ALL
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The database already holds the language: `app_user.preferred_language` is a NOT NULL enum of
 * eight English labels, defaulting to `English`, and it has been there since migration 008. What
 * it does NOT hold is the three things spec 08 §2 asks a screen for:
 *
 *   * the **BCP-47 code** (`ko`), which is what `note.native_lang` stores and what the prompt
 *     names — a column typed `text` and documented as BCP-47 in `packages/shared/types.ts`;
 *   * the **name in its own script** (`한국어`), which §2.1's language pill and §2.3's card
 *     header render — *"the UM's Preferred Language name in its own script"*;
 *   * the **font stack**, because §2.3 requires *"a font stack in with appropriate CJK
 *     fallbacks … The build needs an equivalent per supported script."*
 *
 * Those are presentation facts about a fixed vocabulary, so they belong beside the vocabulary,
 * in the package both the API and the web app already import — not in a migration (they would
 * be eight rows nobody edits), and not in the web app alone (the API templates the same
 * language name into a prompt and into a copy string).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT IS DELIBERATELY NOT HERE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 *   * **No translated chrome.** Q-08b rules that the app's own chrome is translated later, in
 *     its own slice; this table carries the language's NAME, never a translation of a button.
 *   * **No per-language tap-tag labels.** Spec 08 §2.7 draws bilingual chips, and gap A1's
 *     answer is *"one language-neutral tag id underneath, two labels on top"*. The second label
 *     does not exist in this build: `CAPTURE_TAP_TAGS` carries one English `label` and there is
 *     no seeded Korean, Japanese or Hindi one anywhere in the data. Inventing seven
 *     translations here would be inventing the panel's own vocabulary, so the chips render the
 *     English label alone and the review surface says so on screen.
 *   * **No "supported for translation" flag.** The model translates from anything (Q-08a), so a
 *     flag would describe a limit this system does not have. What the eight entries bound is
 *     what a UM can be CONFIGURED as, which is migration 001's enum and not this file's choice.
 */
/**
 * The eight, in migration 001's own order.
 *
 * `English` is in the table and is not a special case: a UM whose Preferred Language is English
 * has an English-first composer (spec 08 §3), and the ONE thing this row is used for is that a
 * bilingual manager switching a single note to native capture picks a language from a list —
 * which must not silently exclude the one they are configured as.
 */
export const PREFERRED_LANGUAGES = Object.freeze([
    Object.freeze({
        name: "English",
        code: "en",
        endonym: "English",
        chip: "EN",
        fontStack: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    }),
    Object.freeze({
        name: "Chinese (Traditional)",
        code: "zh-Hant",
        endonym: "繁體中文",
        chip: "ZH",
        fontStack: '"PingFang TC", "Noto Sans TC", "Microsoft JhengHei", "Heiti TC", system-ui, sans-serif',
    }),
    Object.freeze({
        name: "Japanese",
        code: "ja",
        endonym: "日本語",
        chip: "JA",
        fontStack: '"Hiragino Sans", "Noto Sans JP", "Yu Gothic", "Meiryo", system-ui, sans-serif',
    }),
    Object.freeze({
        name: "Korean",
        code: "ko",
        endonym: "한국어",
        chip: "KO",
        // The mockup's own `.ko` stack (spec 08 §2.3), kept to the face and to the order.
        fontStack: '"Apple SD Gothic Neo", "Noto Sans KR", "Malgun Gothic", "Nanum Gothic", system-ui, sans-serif',
    }),
    Object.freeze({
        name: "Bahasa Malaysia",
        code: "ms",
        endonym: "Bahasa Malaysia",
        chip: "MS",
        fontStack: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    }),
    Object.freeze({
        name: "Bengali",
        code: "bn",
        endonym: "বাংলা",
        chip: "BN",
        fontStack: '"Noto Sans Bengali", "Bangla MN", "Nirmala UI", "Vrinda", system-ui, sans-serif',
    }),
    Object.freeze({
        name: "Hindi",
        code: "hi",
        endonym: "हिन्दी",
        chip: "HI",
        fontStack: '"Noto Sans Devanagari", "Devanagari Sangam MN", "Nirmala UI", "Mangal", system-ui, sans-serif',
    }),
    Object.freeze({
        name: "Thai",
        code: "th",
        endonym: "ไทย",
        chip: "TH",
        fontStack: '"Noto Sans Thai", "Thonburi", "Leelawadee UI", system-ui, sans-serif',
    }),
]);
/**
 * The metadata for a stored `preferred_language` label, or `undefined`.
 *
 * `undefined` rather than a default, deliberately: a label this build does not know means the
 * enum has been widened without this table being told, and a screen that quietly fell back to
 * English would render a Bengali note in a Latin stack and call it English on the pill.
 */
export function languageByName(name) {
    return PREFERRED_LANGUAGES.find((language) => language.name === name);
}
/**
 * The metadata for a BCP-47 code as stored in `note.native_lang`, or `undefined`.
 *
 * Matched case-insensitively, because BCP-47 is case-insensitive by definition and a client
 * that sent `ZH-HANT` named the same language as one that sent `zh-Hant`.
 */
export function languageByCode(code) {
    const wanted = code.toLowerCase();
    return PREFERRED_LANGUAGES.find((language) => language.code.toLowerCase() === wanted);
}
/**
 * Is this a language a note may be captured in? The capture route's validator.
 *
 * **The bound is migration 001's enum and not a translation capability.** The model translates
 * from anything (Q-08a); what is closed is the set of languages a UM can be configured as and
 * therefore the set the app can render an original panel for — a code outside it has no endonym
 * and no font stack, so the review surface could not draw itself.
 */
export function isCaptureLanguage(code) {
    return languageByCode(code) !== undefined;
}
/* ── Spec 08 §6's copy inventory, templated on the language ────────────────── */
/**
 * §2.4's re-translate hint: *"Fixing the Korean re-translates the English."*
 *
 * §6 states the rule for the whole inventory — *"All Korean-specific strings are **templated on
 * the UM's Preferred Language**, e.g. `Fixing the {language} re-translates the English.`"* — and
 * this is the string that rule was written about. The English NAME is used rather than the
 * endonym: the sentence around it is English, and `Fixing the 한국어` reads as a mistake.
 */
export function retranslateHint(language) {
    return `Fixing the ${language.name} re-translates the English.`;
}
/** §2.1's language pill: `한국어 → English`. The endonym, because it names the UM's own side. */
export function languagePill(language) {
    return `${language.endonym} → English`;
}
/** §2.3's card header: `Original (한국어)`. */
export function originalPanelTitle(language) {
    return `Original (${language.endonym})`;
}
/**
 * §2.6's privacy row, line 1: `Your Korean Original Stays Private`.
 *
 * The English name again, for `retranslateHint`'s reason, and Title Case because the whole row
 * is (spec 08 §6). Line 2 is fixed — `Only The English Version Is Released` — and is not
 * templated, because nothing in it names a language.
 */
export function privacyRowTitle(language) {
    return `Your ${language.name} Original Stays Private`;
}
//# sourceMappingURL=native-language.js.map